import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";

interface WorkspaceLike {
  id: string;
  is_public?: boolean | null;
}

/** Pure: is `workspaceId` a public community workspace in the given list. */
export function isWorkspacePublic(
  workspaces: readonly WorkspaceLike[] | undefined,
  workspaceId: string | null | undefined,
): boolean {
  if (!workspaceId) return false;
  return !!(workspaces ?? []).find((w) => w.id === workspaceId)?.is_public;
}

/**
 * Whether the given workspace is a PUBLIC community workspace, plus whether the
 * answer is yet KNOWN. Reads off the user's already-subscribed workspace list
 * (Zero dedups the subscription), so it opens no new sub.
 *
 * `isResolved` lets callers avoid a first-paint flicker: before the workspace
 * list has hydrated (or before `workspaceId` is known) `isPublic` is `false`
 * by default, which would otherwise snap a public-community layout from its
 * private shape to its public one once the query lands.
 */
export function useWorkspacePublicState(
  workspaceId: string | null | undefined,
): {
  isPublic: boolean;
  isResolved: boolean;
} {
  const [workspaces, status] = useQuery(
    workspaceId ? queries.workspaces.byUser() : null,
  );
  return {
    isPublic: isWorkspacePublic(workspaces, workspaceId),
    // Nothing to wait for when there's no workspace to resolve. Otherwise the
    // answer is known once the list query reports complete (or already has rows).
    isResolved: !workspaceId
      ? true
      : status?.type === "complete" || (workspaces?.length ?? 0) > 0,
  };
}
